import { useRef, useState, useEffect } from "react";
import { BeatLoader } from "react-spinners";

const RESEND_DURATION = 300;

const OtpInput = ({ maskedEmail, onVerify, onResend, verifying }) => {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [error, setError] = useState("");
    const [timer, setTimer] = useState(RESEND_DURATION);
    const [resending, setResending] = useState(false);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (timer <= 0) return;
        const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
        return () => clearInterval(interval);
    }, [timer]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    const handleChange = (e, idx) => {
        const value = e.target.value.replace(/[^0-9]/g, "");
        if (!value) return;

        if (value.length === 6) {
            setOtp(value.split("").slice(0, 6));
            inputRefs.current[5]?.focus();
            return;
        }

        const newOtp = [...otp];
        newOtp[idx] = value[0];
        setOtp(newOtp);

        if (idx < 5) inputRefs.current[idx + 1]?.focus();
    };

    const handleKeyDown = (e, idx) => {
        if (e.key === "Backspace") {
            if (otp[idx]) {
                const newOtp = [...otp];
                newOtp[idx] = "";
                setOtp(newOtp);
            } else if (idx > 0) {
                inputRefs.current[idx - 1]?.focus();
            }
        } else if (e.key === "ArrowLeft" && idx > 0) {
            inputRefs.current[idx - 1]?.focus();
        } else if (e.key === "ArrowRight" && idx < 5) {
            inputRefs.current[idx + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        const paste = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
        if (paste.length === 6) {
            setOtp(paste.split("").slice(0, 6));
            inputRefs.current[5]?.focus();
            e.preventDefault();
        }
    };

    const handleResendClick = async () => {
        setResending(true);
        await onResend();
        setTimer(RESEND_DURATION);
        setOtp(["", "", "", "", "", ""]);
        setError("");
        inputRefs.current[0]?.focus();
        setResending(false);
    };

    const handleVerifyClick = async () => {
        const code = otp.join("");
        if (code.length !== 6) {
            setError("Please enter the complete 6-digit code");
            return;
        }
        const isValid = await onVerify(code);
        if (!isValid) {
            setError("Incorrect or expired code");
            setOtp(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
        }
    };

    const isOtpComplete = otp.every((digit) => digit !== "");

    return (
        <div className="flex flex-col items-center px-4 pt-6 w-full">
            <p className="text-[14px] text-center text-black dark:text-white mb-1">
                We've sent a verification code to
            </p>
            <p className="text-[15px] font-semibold text-black dark:text-white mb-6">
                {maskedEmail}
            </p>

            <div className="flex justify-center gap-2 px-2 mb-2">
                {otp.map((digit, idx) => (
                    <input
                        key={idx}
                        type="text"
                        maxLength={1}
                        value={digit}
                        inputMode="numeric"
                        onPaste={handlePaste}
                        autoFocus={idx === 0}
                        onChange={(e) => handleChange(e, idx)}
                        onKeyDown={(e) => handleKeyDown(e, idx)}
                        ref={(el) => (inputRefs.current[idx] = el)}
                        className="w-10 h-10 text-center text-[20px] font-bold bg-white dark:bg-[#333333] text-black dark:text-white border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg focus:border-[#4cb6f9] focus:outline-none transition-colors"
                    />
                ))}
            </div>

            {error && <p className="text-red-500 text-[12px] font-medium mb-2">{error}</p>}

            <div className="flex flex-col items-center gap-2 mb-8 mt-2">
                {timer > 0 ? (
                    <p className="text-[13px] text-gray-600 dark:text-gray-400">
                        Code expires in: <span className="text-[#4cb6f9] font-semibold">{formatTime(timer)}</span>
                    </p>
                ) : (
                    <p className="text-[13px] text-gray-600 dark:text-gray-400">Code expired</p>
                )}

                <div className="text-[13px] text-gray-600 dark:text-gray-400">
                    Didn't receive the code?{" "}
                    <button
                        onClick={handleResendClick}
                        disabled={resending || timer > 0}
                        className="text-[#4cb6f9] font-semibold hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {resending ? "Sending..." : "Resend Code"}
                    </button>
                </div>
            </div>

            <button
                onClick={handleVerifyClick}
                disabled={!isOtpComplete || verifying}
                className={`w-full max-w-md py-3 rounded-lg text-[15px] font-bold transition-all ${isOtpComplete && !verifying
                    ? "bg-[#BDE6FF] text-black cursor-pointer"
                    : "bg-gray-300 dark:bg-[#333333] text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    }`}
            >
                {verifying ? <BeatLoader size={10} color={"#000"} loading={verifying} /> : "Verify Code"}
            </button>
        </div>
    );
};

export default OtpInput;
